import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDashboardPreferences } from '../context/DashboardPreferencesContext';
import { useDashboardStats, useDashboardWidgets } from '../api/queries';
import { getCardById } from '../config/dashboardCardRegistry';
import { DashboardGrid, DashboardConfigurator } from '../components/DashboardConfigurator';
import { fmtCurrency } from '../utils/formatCurrency';
import {
  Building2,
  Users,
  CheckSquare,
  AlertCircle,
  FileText,
  ShoppingCart,
  Clock,
  TrendingUp,
  Target,
  DollarSign,
  Settings2,
} from 'lucide-react';
import MiniPipelineChart from '../components/reports/MiniPipelineChart';
import MiniRevenueChart from '../components/reports/MiniRevenueChart';
import MiniForecastCard from '../components/reports/MiniForecastCard';
import './Dashboard.css';

function Dashboard() {
  const { canDo, isModuleVisible } = useAuth();
  const {
    loading: prefsLoading,
    initializePreferences,
    getVisibleCards,
    getAllCards,
    reorderCards,
  } = useDashboardPreferences();

  const [showConfigurator, setShowConfigurator] = useState(false);

  // From the cache: instant on a return visit, refreshed in the background,
  // and refreshed again by any write to action items, activities, accounts
  // or opportunities anywhere in the app.
  const statsQuery = useDashboardStats();
  const widgetsQuery = useDashboardWidgets();
  const stats = statsQuery.data || null;
  const widgetData = widgetsQuery.data || null;
  const statsLoading = statsQuery.isPending;
  const error = statsQuery.error ? 'Failed to load dashboard data' : '';

  useEffect(() => {
    initializePreferences();
  }, []);

  /**
   * Check if a card should render based on module/permission visibility.
   */
  const isCardPermitted = useCallback((cardId) => {
    const registry = getCardById(cardId);
    if (!registry) return false;
    return isModuleVisible(registry.module) && canDo(registry.module, registry.permission);
  }, [isModuleVisible, canDo]);

  /**
   * Render a card by its ID.
   */
  const renderCard = useCallback((cardId) => {
    if (!isCardPermitted(cardId)) {
      return null;
    }

    switch (cardId) {
      // === Stat Cards ===
      case 'total_accounts':
        return (
          <Link to="/accounts" className="stat-card stat-card-link">
            <div className="stat-card-icon stat-icon-blue">
              <Building2 size={24} />
            </div>
            <div className="stat-card-content">
              <div className="stat-card-label">Total Accounts</div>
              <div className="stat-card-value">{stats?.accounts?.total || 0}</div>
            </div>
          </Link>
        );

      case 'active_contacts':
        return (
          <Link to="/contacts?active=true" className="stat-card stat-card-link">
            <div className="stat-card-icon stat-icon-green">
              <Users size={24} />
            </div>
            <div className="stat-card-content">
              <div className="stat-card-label">Active Contacts</div>
              <div className="stat-card-value">{stats?.contacts?.active || 0}</div>
            </div>
          </Link>
        );

      case 'open_tasks':
        return (
          <Link to="/action-items?status=Open" className="stat-card stat-card-link">
            <div className="stat-card-icon stat-icon-amber">
              <CheckSquare size={24} />
            </div>
            <div className="stat-card-content">
              <div className="stat-card-label">Open Action Items</div>
              <div className="stat-card-value">{stats?.tasks?.open || 0}</div>
            </div>
          </Link>
        );

      case 'overdue_tasks':
        return (
          <Link to="/action-items?status=Open&overdue=true" className="stat-card stat-card-link">
            <div className="stat-card-icon stat-icon-red">
              <AlertCircle size={24} />
            </div>
            <div className="stat-card-content">
              <div className="stat-card-label">Overdue Action Items</div>
              <div className="stat-card-value">{stats?.tasks?.overdue || 0}</div>
            </div>
          </Link>
        );

      case 'open_opportunities':
        return (
          <Link to="/opportunities?stage=open" className="stat-card stat-card-link">
            <div className="stat-card-icon stat-icon-violet">
              <Target size={24} />
            </div>
            <div className="stat-card-content">
              <div className="stat-card-label">Open Opportunities</div>
              <div className="stat-card-value">{stats?.opportunities?.open || 0}</div>
            </div>
          </Link>
        );

      case 'pipeline_value':
        return (
          <Link to="/opportunities?stage=open" className="stat-card stat-card-link">
            <div className="stat-card-icon stat-icon-violet">
              <DollarSign size={24} />
            </div>
            <div className="stat-card-content">
              <div className="stat-card-label">Pipeline Value</div>
              <div className="stat-card-value">${fmtCurrency(stats?.opportunities?.openValue)}</div>
            </div>
          </Link>
        );

      case 'won_opportunities':
        return (
          <Link to="/opportunities?stage=won" className="stat-card stat-card-link">
            <div className="stat-card-icon stat-icon-green">
              <Target size={24} />
            </div>
            <div className="stat-card-content">
              <div className="stat-card-label">Won Opportunities</div>
              <div className="stat-card-value">{stats?.opportunities?.won || 0}</div>
            </div>
          </Link>
        );

      case 'won_value':
        return (
          <Link to="/opportunities?stage=won" className="stat-card stat-card-link">
            <div className="stat-card-icon stat-icon-green">
              <DollarSign size={24} />
            </div>
            <div className="stat-card-content">
              <div className="stat-card-label">Won Value</div>
              <div className="stat-card-value">${fmtCurrency(stats?.opportunities?.wonValue)}</div>
            </div>
          </Link>
        );

      // === Pipeline Cards ===
      case 'account_pipeline':
        return (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Account Pipeline</h3>
            </div>
            <div className="card-body">
              {stats?.accounts?.byStage?.length > 0 ? (
                <div className="pipeline-stages">
                  {stats.accounts.byStage.map((stage) => (
                    <Link
                      key={stage.stage}
                      to={`/accounts?stage=${encodeURIComponent(stage.stage)}`}
                      className="pipeline-stage pipeline-stage-link"
                    >
                      <div className="pipeline-stage-header">
                        <span className="pipeline-stage-name">{stage.stage}</span>
                        <span className="pipeline-stage-count">{stage.count}</span>
                      </div>
                      <div className="pipeline-stage-bar">
                        <div
                          className="pipeline-stage-fill"
                          style={{
                            width: `${stats.accounts.total > 0 ? (stage.count / stats.accounts.total) * 100 : 0}%`,
                          }}
                        />
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="empty-state"><p>No accounts yet</p></div>
              )}
            </div>
          </div>
        );

      case 'opportunity_pipeline':
        return (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Opportunity Pipeline</h3>
              <Link to="/opportunities" className="text-sm">View all</Link>
            </div>
            <div className="card-body">
              {stats?.opportunities?.byStage?.length > 0 ? (
                <div className="pipeline-stages">
                  {stats.opportunities.byStage.map((stage) => (
                    <Link
                      key={stage.stage}
                      to={`/opportunities?stage_name=${encodeURIComponent(stage.stage)}`}
                      className="pipeline-stage pipeline-stage-link"
                    >
                      <div className="pipeline-stage-header">
                        <span className="pipeline-stage-name">
                          {stage.stage}
                          {stage.is_won ? ' (Won)' : stage.is_lost ? ' (Lost)' : ''}
                        </span>
                        <span className="pipeline-stage-count">
                          {stage.count} (${fmtCurrency(stage.total_value)})
                        </span>
                      </div>
                      <div className="pipeline-stage-bar">
                        <div
                          className={`pipeline-stage-fill ${stage.is_won ? 'pipeline-stage-fill-won' : stage.is_lost ? 'pipeline-stage-fill-lost' : ''}`}
                          style={{
                            width: `${stats.opportunities.total > 0 ? (stage.count / stats.opportunities.total) * 100 : 0}%`,
                            backgroundColor: stage.is_won ? '#22c55e' : stage.is_lost ? '#ef4444' : undefined
                          }}
                        />
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="empty-state"><p>No opportunities yet</p></div>
              )}
            </div>
          </div>
        );

      // === Action Item & Summary Cards ===
      case 'upcoming_tasks':
        return (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Upcoming Action Items</h3>
              <Link to="/action-items" className="text-sm">View all</Link>
            </div>
            <div className="card-body">
              {stats?.tasks?.upcoming?.length > 0 ? (
                <div className="task-list">
                  {stats.tasks.upcoming.map((task) => (
                    <Link key={task.id} to="/action-items" className="task-item task-item-link">
                      <div className="task-item-icon"><Clock size={16} /></div>
                      <div className="task-item-content">
                        <div className="task-item-title">{task.title}</div>
                        <div className="task-item-meta">
                          {task.account_name && <span>{task.account_name}</span>}
                          {task.due_date && (
                            <span className={new Date(task.due_date) < new Date() ? 'text-danger' : ''}>
                              Due: {new Date(task.due_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="empty-state"><p>No upcoming action items</p></div>
              )}
            </div>
          </div>
        );

      case 'quotes_summary':
        return (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title"><FileText size={18} className="mr-2" />Quotes Summary</h3>
              <Link to="/quotes" className="text-sm">View all</Link>
            </div>
            <div className="card-body">
              <div className="summary-stats">
                <div className="summary-stat">
                  <span className="summary-stat-value">{stats?.quotes?.total || 0}</span>
                  <span className="summary-stat-label">Total Quotes</span>
                </div>
                <div className="summary-stat">
                  <span className="summary-stat-value">${fmtCurrency(stats?.quotes?.totalValue)}</span>
                  <span className="summary-stat-label">Total Value</span>
                </div>
              </div>
              {stats?.quotes?.byStatus?.length > 0 && (
                <div className="status-breakdown">
                  {stats.quotes.byStatus.map((s) => (
                    <Link key={s.status} to={`/quotes?status=${encodeURIComponent(s.status)}`} className="status-item status-item-link">
                      <span className={`badge badge-${getStatusColor(s.status)}`}>{s.status}</span>
                      <span>{s.count}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 'orders_summary':
        return (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title"><ShoppingCart size={18} className="mr-2" />Orders Summary</h3>
              <Link to="/orders" className="text-sm">View all</Link>
            </div>
            <div className="card-body">
              <div className="summary-stats">
                <div className="summary-stat">
                  <span className="summary-stat-value">{stats?.orders?.total || 0}</span>
                  <span className="summary-stat-label">Total Orders</span>
                </div>
                <div className="summary-stat">
                  <span className="summary-stat-value">${fmtCurrency(stats?.orders?.totalValue)}</span>
                  <span className="summary-stat-label">Total Value</span>
                </div>
              </div>
              {stats?.orders?.byStatus?.length > 0 && (
                <div className="status-breakdown">
                  {stats.orders.byStatus.map((s) => (
                    <Link key={s.status} to={`/orders?status=${encodeURIComponent(s.status)}`} className="status-item status-item-link">
                      <span className={`badge badge-${getOrderStatusColor(s.status)}`}>{s.status}</span>
                      <span>{s.count}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      // === Activity Card ===
      case 'recent_activity':
        return (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Recent Activity</h3>
              <Link to="/activities" className="text-sm">View all</Link>
            </div>
            <div className="card-body">
              {stats?.activities?.recent?.length > 0 ? (
                <div className="activity-list">
                  {stats.activities.recent.map((activity) => (
                    <div key={activity.id} className="activity-item">
                      <div className={`activity-icon activity-icon-${activity.type.toLowerCase()}`}>
                        <TrendingUp size={14} />
                      </div>
                      <div className="activity-content">
                        <div className="activity-header">
                          <span className="activity-type">{activity.type}</span>
                          <span className="activity-date">{new Date(activity.activity_date).toLocaleString()}</span>
                        </div>
                        <div className="activity-details">
                          {activity.account_name && (
                            <Link to={`/accounts/${activity.account_id}`}>{activity.account_name}</Link>
                          )}
                          {activity.contact_name && <span> - {activity.contact_name}</span>}
                        </div>
                        {activity.description && (
                          <div className="activity-description">{activity.description}</div>
                        )}
                        <div className="activity-logged-by">
                          by {activity.logged_by_name}
                          {activity.updated_by_name && (
                            <span style={{ marginLeft: '6px', color: 'var(--text-tertiary)', fontSize: '11px', fontStyle: 'italic' }}>
                              (edited)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state"><p>No recent activity</p></div>
              )}
            </div>
          </div>
        );

      // === Report Widget Cards ===
      case 'pipeline_chart':
        return (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Pipeline Overview</h3>
            </div>
            <div className="card-body">
              <MiniPipelineChart data={widgetData?.pipeline} />
            </div>
          </div>
        );

      case 'revenue_trend':
        return (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Revenue Trend</h3>
            </div>
            <div className="card-body">
              <MiniRevenueChart widgetData={widgetData} />
            </div>
          </div>
        );

      case 'forecast_summary':
        return (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Forecast Summary</h3>
            </div>
            <div className="card-body">
              <MiniForecastCard widgetData={widgetData} />
            </div>
          </div>
        );

      default:
        return null;
    }
  }, [stats, widgetData, isCardPermitted]);

  // Filter visible cards to only those the user has permission to see
  const visibleCards = getVisibleCards().filter(c => isCardPermitted(c.id));
  const allCards = getAllCards();

  // Loading state with skeleton
  if (statsLoading || prefsLoading) {
    return (
      <div className="dashboard">
        <div className="page-header">
          <h1 className="page-title">Dashboard</h1>
        </div>
        <div className="dashboard-skeleton">
          <div className="dashboard-skeleton-stats">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="skeleton-card skeleton-stat">
                <div className="skeleton-shimmer" />
              </div>
            ))}
          </div>
          <div className="dashboard-skeleton-content">
            {[1, 2].map(i => (
              <div key={i} className="skeleton-card skeleton-half">
                <div className="skeleton-shimmer" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-error" role="alert" aria-live="assertive">{error}</div>;
  }

  // Empty state when all cards hidden
  if (visibleCards.length === 0) {
    return (
      <div className="dashboard">
        <div className="page-header">
          <h1 className="page-title">Dashboard</h1>
          <div className="page-actions">
            <button className="btn btn-secondary dashboard-customize-btn" onClick={() => setShowConfigurator(true)}>
              <Settings2 size={18} />
              Customize Dashboard
            </button>
          </div>
        </div>
        <div className="dashboard-empty-state">
          <h3>Your dashboard is empty</h3>
          <p>Add cards back to see your CRM data at a glance.</p>
          <button className="btn btn-primary" onClick={() => setShowConfigurator(true)}>
            Customize Dashboard
          </button>
        </div>
        <DashboardConfigurator isOpen={showConfigurator} onClose={() => setShowConfigurator(false)} />
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <div className="page-actions">
          <button className="btn btn-secondary dashboard-customize-btn" onClick={() => setShowConfigurator(true)}>
            <Settings2 size={18} />
            Customize Dashboard
          </button>
        </div>
      </div>

      <DashboardGrid
        visibleCards={visibleCards}
        allCards={allCards}
        onReorder={reorderCards}
        renderCard={renderCard}
      />

      <DashboardConfigurator isOpen={showConfigurator} onClose={() => setShowConfigurator(false)} />
    </div>
  );
}

function getStatusColor(status) {
  switch (status) {
    case 'Draft': return 'gray';
    case 'Sent': return 'primary';
    case 'Accepted': return 'success';
    case 'Rejected': return 'danger';
    default: return 'gray';
  }
}

function getOrderStatusColor(status) {
  switch (status) {
    case 'Order Created': return 'warning';
    case 'Shipped': return 'success';
    default: return 'gray';
  }
}

export default Dashboard;
